// Login page for Sunlight Grounds Care Client Portal
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useProperty } from '@/hooks/useProperty';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { refreshProperties, clearSelection } = useProperty();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsPending(false);
    setLoading(true);

    // Clear any stale property data from previous session
    clearSelection();

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      // Reload properties from the real backend now that api_mode is set
      await refreshProperties();

      const tabRoutes = {
        'landscape_overview': '/overview',
        'overview': '/overview',
        'landscape_schedule': '/schedule',
        'landscape_photos': '/delivery',
        'communication': '/comms',
        'communications': '/comms',
      };
      const firstTab = result.user?.allowed_tabs?.[0];
      // Full page reload so AppRoot remounts and reads the fresh user/tabs from localStorage
      window.location.href = tabRoutes[firstTab] || '/overview';
    } else if (result.isPending) {
      setIsPending(true);
    } else {
      setError(result.error);
    }
  };

  // Full screen pending message
  if (isPending) {
    return (
      <div className="min-h-screen bg-navy flex flex-col items-center justify-center p-6" data-testid="pending-screen">
        <img 
          src="/images/shield.png" 
          alt="Sunlight Grounds Care" 
          className="h-24 w-auto"
        />
        <h1 className="font-dm text-xl font-semibold text-white text-center mt-6 mb-4 uppercase tracking-wide">
          Account Pending
        </h1>
        <p className="text-white/70 text-center font-dm text-sm leading-relaxed max-w-[280px]">
          Contact your manager to activate your account
        </p>
        <button
          onClick={() => setIsPending(false)}
          className="mt-8 text-gold text-sm font-dm underline"
          data-testid="back-to-login-btn"
        >
          Back to login
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top)' }} data-testid="login-page">
      {/* Logo section */}
      <div className="flex-1 flex flex-col items-center justify-center pt-16 pb-8">
        {/* Use the actual brand logo PNG - white version for dark background */}
        <img 
          src="/images/logo-white.png" 
          alt="Sunlight Grounds Care" 
          className="h-28 w-auto"
          data-testid="login-logo"
        />
        {/* Tagline */}
        <p className="text-white/80 text-[13px] font-dm text-center mt-6">
          Refined Exterior Care. Thoughtfully Managed.
        </p>
      </div>

      {/* Login form */}
      <div className="bg-cream rounded-t-[28px] p-6 pt-8 pb-10 sm:max-w-lg sm:mx-auto sm:rounded-[28px] sm:mb-8" data-testid="login-form-container">
        <h2 className="font-cormorant text-xl font-semibold text-navy mb-6 text-center">
          Welcome back
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-dm text-slate uppercase tracking-wider mb-2 font-medium">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-black/[0.08] bg-slate-light 
                         font-dm text-sm text-navy placeholder:text-slate/50
                         focus:outline-none focus:border-navy/30 transition-colors"
              placeholder="your@email.com"
              required
              data-testid="email-input"
            />
          </div>

          <div>
            <label className="block text-[10px] font-dm text-slate uppercase tracking-wider mb-2 font-medium">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-black/[0.08] bg-slate-light 
                         font-dm text-sm text-navy placeholder:text-slate/50
                         focus:outline-none focus:border-navy/30 transition-colors"
              placeholder="••••••••"
              required
              data-testid="password-input"
            />
          </div>

          {error && (
            <p className="text-red-600 text-xs font-dm text-center" data-testid="login-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-navy text-white font-dm text-sm font-medium py-3 rounded-xl
                       hover:bg-navy-light transition-colors disabled:opacity-60 mt-2"
            data-testid="login-submit-btn"
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <p className="text-center text-slate text-xs font-dm mt-6">
          Need help? Contact your property manager
        </p>
      </div>
    </div>
  );
}
